/**
 * Dynamic Module Loader
 *
 * Handles dynamic importing of the internal SDK bundle.
 * Caches the module after first load and provides loading/error states.
 */

import type { SdkInternalModule } from './sdkContract.js';

/**
 * Loading state for the internal module
 */
export type LoadingState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; module: SdkInternalModule }
  | { status: 'error'; error: Error };

// Module cache keyed by baseURL — ensures each URL gets its own cached entry
const moduleCache = new Map<string, SdkInternalModule>();
const loadingPromises = new Map<string, Promise<SdkInternalModule>>();

/**
 * Load the internal SDK module via dynamic import.
 *
 * The module is cached after first successful load.
 * Concurrent calls to loadInternalModule() will share the same loading promise.
 *
 * @param baseURL - Base URL where the internal bundle is hosted (e.g., https://ai.ogov.me)
 * @returns Promise resolving to the internal module
 *
 * @example
 * ```typescript
 * const internal = await loadInternalModule('https://ai.ogov.me');
 * internal.mount(container, options);
 * ```
 */
export async function loadInternalModule(
  baseURL: string,
  importFn: (url: string) => Promise<unknown> = (url) => import(/* webpackIgnore: true */ url)
): Promise<SdkInternalModule> {
  // Return cached module if available
  const cached = moduleCache.get(baseURL);
  if (cached) {
    return cached;
  }

  // Return in-progress loading promise if available (deduplication)
  const existing = loadingPromises.get(baseURL);
  if (existing) {
    return existing;
  }

  // Start new load
  const promise = (async () => {
    try {
      // Construct the module URL
      // The internal bundle is served at /sdk-internal/index.js
      const moduleUrl = `${baseURL}/sdk-internal/index.js`;

      // Dynamic import
      const module = await importFn(moduleUrl);

      // Extract the default export (which is SdkInternalModule)
      const internalModule = (module as { default: unknown }).default as SdkInternalModule;

      // Validate the module has the expected interface
      if (
        typeof internalModule.mount !== 'function' ||
        typeof internalModule.unmount !== 'function' ||
        typeof internalModule.update !== 'function' ||
        typeof internalModule.canUseOgAssist !== 'function'
      ) {
        throw new Error('Invalid internal module: missing required functions');
      }

      // Cache the module
      moduleCache.set(baseURL, internalModule);

      return internalModule;
    } catch (error) {
      // Clear loading promise on error so retry is possible
      loadingPromises.delete(baseURL);

      // Re-throw with more context
      throw new Error(
        `Failed to load internal SDK module from ${baseURL}/sdk-internal/index.js: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  })();

  loadingPromises.set(baseURL, promise);

  return promise;
}

/**
 * Clear the module cache (for testing/development).
 *
 * @param baseURL - If provided, clears only the cache for that URL. Otherwise clears all.
 */
export function clearModuleCache(baseURL?: string): void {
  if (baseURL) {
    moduleCache.delete(baseURL);
    loadingPromises.delete(baseURL);
  } else {
    moduleCache.clear();
    loadingPromises.clear();
  }
}

/**
 * Check if the internal module is already loaded.
 *
 * @param baseURL - If provided, checks only that URL. Otherwise checks if any module is cached.
 * @returns true if module is cached, false otherwise
 */
export function isModuleLoaded(baseURL?: string): boolean {
  if (baseURL) {
    return moduleCache.has(baseURL);
  }
  return moduleCache.size > 0;
}
